// Which backend Writings talks to. The rule is strict on purpose: Writings may
// only ever reach the DEV Supabase project. Anything else — production, an
// unknown project, missing env — falls back to local demo data rather than
// risk writing to the wrong database.

export const DEV_PROJECT_REF = 'nlsqtuxvpffpqnwsrwed'
export const PROD_PROJECT_REF = 'ipdohaujoakjhfufgxje'

/** localStorage key that forces the local demo backend (e.g. while dev is down). */
export const BACKEND_OVERRIDE_KEY = 'burrow-writings-backend'

export type BackendConfig =
  | { mode: 'supabase'; url: string; anonKey: string }
  | { mode: 'local'; reason: string }

type Env = Record<string, string | boolean | undefined>

export function resolveBackendConfig(env: Env, override?: string | null): BackendConfig {
  if (override === 'local' || env.VITE_WRITINGS_BACKEND === 'local') {
    return { mode: 'local', reason: 'Local demo data was chosen.' }
  }

  const url = String(env.VITE_WRITINGS_SUPABASE_URL || env.VITE_SUPABASE_URL || '')
  const anonKey = String(env.VITE_WRITINGS_SUPABASE_ANON_KEY || env.VITE_SUPABASE_ANON_KEY || '')
  if (!url || !anonKey) {
    return { mode: 'local', reason: 'No Supabase URL or key is configured.' }
  }

  let host: string
  try {
    host = new URL(url).hostname
  } catch {
    return { mode: 'local', reason: 'The Supabase URL is not a valid URL.' }
  }

  if (host.split('.')[0] === PROD_PROJECT_REF) {
    return { mode: 'local', reason: 'Refusing to connect Writings to the production database.' }
  }
  const isDev = host === `${DEV_PROJECT_REF}.supabase.co`
  const isLocalStack = host === 'localhost' || host === '127.0.0.1'
  if (!isDev && !isLocalStack) {
    return { mode: 'local', reason: `Writings only connects to the dev project (${DEV_PROJECT_REF}).` }
  }

  return { mode: 'supabase', url, anonKey }
}

function readOverride(): string | null {
  try {
    return window.localStorage.getItem(BACKEND_OVERRIDE_KEY)
  } catch {
    return null
  }
}

export function setBackendOverride(value: 'local' | null): void {
  try {
    if (value) window.localStorage.setItem(BACKEND_OVERRIDE_KEY, value)
    else window.localStorage.removeItem(BACKEND_OVERRIDE_KEY)
  } catch {
    /* storage unavailable — nothing to persist */
  }
}

export const backendOverrideActive: boolean = readOverride() === 'local'

/** The single-file prototype build (`npm run prototype:writings`). */
export const isPrototype: boolean = typeof __WRITINGS_PROTOTYPE__ !== 'undefined' && __WRITINGS_PROTOTYPE__ === true

export const backendConfig: BackendConfig = isPrototype
  ? { mode: 'local', reason: 'Prototype build — local demo data only.' }
  : resolveBackendConfig(import.meta.env, readOverride())

/** The main Burrow app, for the Library / Thinking Space / Collections links. */
export const burrowAppUrl: string = isPrototype
  ? 'https://app.burrow.world'
  : String(import.meta.env.VITE_BURROW_APP_URL || 'http://localhost:5173').replace(/\/$/, '')

/** Origin shown in (and used for) public writing links. */
export function publicOrigin(): string {
  if (isPrototype) return 'https://burrow.world' // a file:// page has no origin
  const configured = import.meta.env.VITE_WRITINGS_PUBLIC_ORIGIN
  return String(configured || window.location.origin).replace(/\/$/, '')
}
